import { OrderState } from 'app/order/models/order-state.model';
import { PageMeta } from '../page-meta';
import { ContractWidgetConfig } from './contract-widgets';
import { InsurantsFormConfig } from './insurants-form-config';
import { InsuredObjectFormConfig } from './insured-object-form-config';
import { OrderStepConfig } from './order-step';
import { DamageReportStep, DamageReportStepConfig } from 'app/customer-portal/damage-reports';
import { DamageReportValidatorConfig } from './damage-report';
import { Tag } from './tag';

export interface ProductPresentation {
    description: string;
    bulletPoints: string[];
    pageTitle?: string;

    imageUrl: string;
    imageAlt: string;
    imageHeadline: string;

    docIPIDUrl: string;
    docAVBUrl: string;
    docAddInfoUrl: string;
    docAnzeigepflichtUrl?: string;

    meta?: PageMeta;
}

export interface OrderProductConfig {
    /**
     * @description array of form step configs. customer
     *  will complete form steps in the given order.
     */
    steps: OrderStepConfig[];

    /**
     * @description displayed next to product price. Use this
     *  to show that a product is tax exempt or price
     *  includes VAT
     * @default ''
     */
    taxInfo?: string;

    /**
     * @description max allowed age of insurant.
     * @default 99
     */
    insurantMaxAge?: number;

    /**
     * @description if true customers must consent to transfer of
     *  medical data
     * @default false
     */
    requiresMedicalData?: boolean;

    /**
     * @description if true customers must declare that insured
     *  objects where not damaged before the insurance
     *  contract start
     * @default false
     */
    requiresNoPriorDefect?: boolean;

    /**
     * @description if true, price will not be displayed
     *  on product description page, since it will
     *  be calculated by InsureNet
     * @default false
     */
    hasFlexPrice?: boolean;

    /**
     * @description if true, price on description page
     *  will be prefixed with "ab"
     * @default false
     */
    showPriceFrom?: boolean;

    /**
     * @description if true, input for discount codes
     *  is displayed on submit page
     * @default true
     */
    showDiscountInput?: boolean;

    /**
     * @description if true, only titles for natural
     *  persons (Herr/Frau) are shown on the
     *  billing address form. Customers logged
     *  in with a company account are not
     *  allowed to order this product. This
     *  will always be false when companiesOnly
     *  is set to true.
     * @default true
     */
    naturalPersonsOnly?: boolean;

    /**
     * @description if true, the title selection input
     *  on the billing address form is hidden
     *  and defaults to company. Customers logged
     *  in with a non-company account are not
     *  allowed to order this product. If this
     *  is set to true naturalPersonsOnly will
     *  be set to false.
     * @default false
     */
    companiesOnly?: boolean;

    /**
     * @description set to true when the sidebar should
     *  be hidden on the product landing page.
     *  Used for custom product landing pages.
     * @default false
     */
    hideSummarySidebarOnLandingPage?: boolean;

    /**
     * @description should insurantsFamilyRelationship
     *  be set to true by default when product can
     *  have only one insurant.
     *  (for multiple insurants use
     *  InsurantsFormConfig.requiresInsurantsFamilyRelationship)
     * @default false
     */
    setFamilyRelationshipOnSingleInsurant?: boolean;

    /**
     * @description how many months in advance of
     *  the contract start an order is
     *  allowed.
     * @default 12
     */
    monthsInAdvance?: number;

    /**
     * @description set to 'day' when customer should
     *  be able to select a precise date for
     *  contract start. Set to 'month' if
     *  customer only should only be able to
     *  select a starting month.
     * @default 'day'
     */
    contractStartMode?: 'day' | 'month';

    /**
     * @description set to 'default' if normal
     *  header and footer should be visible.
     *  Use the appropriate keyword for
     *  product specific header and footer.
     * @default 'default'
     */
    headerMode?: 'default';

    /**
     * @description Set to true to display the message
     * that the first payment will be taken within 10
     * days
     * @default false
     */
    showTenDayFirstPaymentMessage?: boolean;

    /**
     * @description Set to true to disable the date
     * of birth input if there is an insuree
     * @default false
     */
    disableBirthdayInBillingAddress?: boolean;

    /**
     * @description If there is more than one product
     * for the same `InsuranceType` you can use the
     * `canonical` property to determine which product
     * should be selected for the `InsuranceType`. If
     * `canonical` is not set on any of the products
     * the first product provided will be selected.
     * This is mostly relevant in the context of the service
     * portal where we only get the `InsuranceType`s with
     * the customer `Contract`s and need to determine
     * which product to display for each `Contract`.
     * @default false
     */
    canonical?: boolean;
}

export interface PortalProductConfig {
    /**
     * @description Text is used in contract list item if there are open damages.
     * @see {@link ContractListItemComponent}
     * @default 'Schadensmeldung in Bearbeitung'
     */
    openDamageLabel?: string;

    /**
     * @description Name of the icon that is used for the
     * list item in the contract list.
     * @see icon.config.ts
     * @default ''
     */
    icon?: string;

    /**
     * @description override for the default icon size of 52px.
     * Use this when the default icon size does make problems.
     * @default 52px
     */
    iconSize?: `${number}px`;

    /**
     * @description List of widgets to be used for the
     * contract detail view. Falls back to the default
     * widgets ({@link defaultContractWidgets }) when
     * not defined.
     * @see default-contract-widgets.ts
     * @default undefined
     */
    widgets?: ContractWidgetConfig[];

    /**
     * @description Name of the icon that is used for the
     * damage report button.
     * @see {@link DamageReportButtonComponent}
     * @default 'damage'
     */
    reportDamageButtonIcon?: string;

    /**
     * @description Name of the label that is used for the
     * damage report button.
     * @see {@link DamageReportButtonComponent}
     * @default 'Schaden melden'
     */
    reportDamageButtonLabel?: string;

    /**
     * @description Use this to configure the damage
     * report process for the product. If this is not set
     * the damage report will use the default process.
     * @default undefined
     */
    damageReportSteps?: DamageReportStepConfig<DamageReportStep>[];

    /**
     * @description Use this to configure non-standard validator
     * settings for the damage report form. If this is not
     * set the default validators will be used.
     * @default undefined
     */
    damageReportValidators?: Partial<DamageReportValidatorConfig>;
}

export type ProductOverrides = Partial<{ [Key in keyof Product]: Partial<Product[Key]> }>;

export interface ProductVariant {
    condition: (state: OrderState) => boolean;
    overrides: ProductOverrides;
}

export interface ProductWithVariants {
    slug: string;
    title: string;
    fullTitle: string;
    collapsableTitle: string;
    insuranceTypeName: string;
    insuranceTypeId: number;
    legacy: boolean;
    presentation: ProductPresentation | undefined;
    orderConfig: OrderProductConfig;
    portalConfig: PortalProductConfig;
    insuredObjectsForm: InsuredObjectFormConfig | undefined;
    insurantsForm: InsurantsFormConfig | undefined;
    tags?: Tag[];
    variants?: ProductVariant[];
    onInit?: (state: OrderState, product: Product) => Partial<OrderState>;
}

export type Product = Omit<ProductWithVariants, 'variants'>;

export type ProductConfig = {
    slug: string;
    title: string;
    insuranceTypeId: number;
    fullTitle?: string;
    collapsableTitle?: string;
    insuranceTypeName?: string;
    legacy?: boolean;
    presentation?: ProductPresentation | undefined;
    orderConfig?: OrderProductConfig;
    portalConfig?: PortalProductConfig;
    insuredObjectsForm?: InsuredObjectFormConfig | undefined;
    insurantsForm?: InsurantsFormConfig | undefined;
    variants?: ProductVariant[];
    tags?: Tag[];
    onInit?: (state: OrderState, product: Product) => Partial<OrderState>;
};

export function getProductWithVariants(config: ProductConfig): ProductWithVariants {
    const fullTitle = config.fullTitle ?? config.title;

    return {
        slug: config.slug,
        title: config.title,
        fullTitle: fullTitle,
        collapsableTitle: config.collapsableTitle ?? fullTitle,
        insuranceTypeName: config.insuranceTypeName ?? '',
        insuranceTypeId: config.insuranceTypeId,
        legacy: config.legacy ?? false,
        presentation: config.presentation,
        orderConfig: getOrderProductConfig(config),
        portalConfig: { ...config.portalConfig },
        insuredObjectsForm: getInsuredObjectFormConfig(config),
        insurantsForm: config.insurantsForm,
        variants: config.variants,
        tags: config.tags ?? [],
        onInit: config.onInit,
    };
}

function getOrderProductConfig(config: ProductConfig): OrderProductConfig {
    if (!config.orderConfig) {
        return undefined;
    }
    const orderConfig = config.orderConfig;
    return {
        steps: orderConfig.steps,
        taxInfo: orderConfig.taxInfo ?? '',
        requiresMedicalData: orderConfig.requiresMedicalData ?? false,
        hasFlexPrice: orderConfig.hasFlexPrice ?? false,
        showPriceFrom: orderConfig.showPriceFrom ?? false,
        showDiscountInput: orderConfig.showDiscountInput ?? true,
        // will be overridden by companies only setting, since they are mutualy exclusive:
        naturalPersonsOnly: orderConfig.companiesOnly === true ? false : (orderConfig.naturalPersonsOnly ?? true),
        companiesOnly: orderConfig.companiesOnly ?? false,
        insurantMaxAge: orderConfig.insurantMaxAge ?? 99,
        monthsInAdvance: orderConfig.monthsInAdvance ?? 12,
        contractStartMode: orderConfig.contractStartMode ?? 'day',
        hideSummarySidebarOnLandingPage: orderConfig.hideSummarySidebarOnLandingPage ?? false,
        headerMode: orderConfig.headerMode ?? 'default',
        requiresNoPriorDefect: orderConfig.requiresNoPriorDefect ?? false,
        setFamilyRelationshipOnSingleInsurant: orderConfig.setFamilyRelationshipOnSingleInsurant ?? false,
        showTenDayFirstPaymentMessage: orderConfig.showTenDayFirstPaymentMessage ?? false,
        disableBirthdayInBillingAddress: orderConfig.disableBirthdayInBillingAddress ?? false,
        canonical: orderConfig.canonical ?? false,
    };
}

function getInsuredObjectFormConfig(config: ProductConfig): InsuredObjectFormConfig {
    if (!config.insuredObjectsForm) {
        return undefined;
    }
    const form = config.insuredObjectsForm;
    return {
        controls: form.controls,
        max: form.max ?? -1,
        headline: form.headline,
        description: form.description,
        objectDescriptionSingular: form.objectDescriptionSingular,
        objectDescriptionPlural: form.objectDescriptionPlural ?? form.objectDescriptionSingular,
        objectInfoControl: form.objectInfoControl ?? form.controls[0].name,
        addButtonLabel: form.addButtonLabel ?? 'hinzufügen',
        note: form.note,
        additionalComponents: form.additionalComponents,
    };
}

/**
 * @description merges a `Product` with a `ProductOverrides` object. It will also merge second level
 * objects so you can override only some of their properties
 * @param {Product} product the base product
 * @param {ProductOverrides} overrides the overrides to be merged into the product
 * @returns {Product} combined product and overrides
 */
export function mergeProductOverrides<T extends Product>(product: T, overrides: ProductOverrides): T {
    const merged = { ...product };

    for (const key in overrides) {
        if (!Object.hasOwn(overrides, key)) {
            continue;
        }
        if (overrides[key] != null && typeof overrides[key] === 'object' && !Array.isArray(overrides[key])) {
            merged[key] = { ...merged[key], ...overrides[key] };
        } else {
            merged[key] = overrides[key];
        }
    }
    return merged;
}

export function isProduct(x: unknown): x is Product {
    return (
        typeof x === 'object' &&
        'slug' in x &&
        typeof x.slug === 'string' &&
        'title' in x &&
        typeof x.title === 'string' &&
        'collapsableTitle' in x &&
        typeof x.collapsableTitle === 'string' &&
        'insuranceTypeName' in x &&
        typeof x.insuranceTypeName === 'string' &&
        'insuranceTypeId' in x &&
        typeof x.insuranceTypeId === 'number'
    );
}
